import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { useDocumentViewer } from '../contexts/DocumentViewerContext'
import PTORequestModal from '../components/PTORequestModal'
import EEAPModal from '../components/EEAPModal'
import ProfileAdvancesTab from '../components/ProfileAdvancesTab'
// Reviews temporarily disabled for HIPAA compliance phase
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faUser, faIdCard, faBriefcase, faGraduationCap, faFileAlt, faEdit, faSave, faTimes, faPhone, faEnvelope, faMapMarkerAlt, faCalendarAlt, faBuilding, faUserTie, faDollarSign, faShieldAlt, faInfoCircle, faStar, faChevronDown, faChevronRight, faFolder, faFolderOpen, faFilePdf, faFileWord, faFileExcel, faFileImage, faDownload, faExternalLinkAlt, faGlobe, faSpinner, faReceipt } from '@fortawesome/free-solid-svg-icons'
import { useAuth } from '../contexts/AuthContext'
import { profileService, type EmployeeProfile, type ContactEntry, type EmployeeCert, certDisplayLabel } from '../services/profileService'
import DaisyDatePicker from '../components/DaisyDatePicker'
import { useConfirmPassword } from '../components/ConfirmPasswordModal'

interface ProfilePageProps {
}

function ProfileQuickAction({
  icon,
  title,
  description,
  onClick,
}: {
  icon: IconDefinition
  title: string
  description: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 w-full rounded-box border border-base-300 bg-base-100 px-3 py-2.5 text-left hover:bg-base-200/70 hover:border-base-content/20"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <FontAwesomeIcon icon={icon} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold leading-tight">{title}</span>
        <span className="block text-xs text-base-content/60 font-normal">{description}</span>
      </span>
      <FontAwesomeIcon icon={faChevronRight} className="text-xs text-base-content/40 shrink-0" />
    </button>
  )
}

const ProfilePage: React.FC<ProfilePageProps> = () => {
  const navigate = useNavigate()
  const { openDocument } = useDocumentViewer()
  const { loading: authLoading, hasPermission, user } = useAuth()
  
  // Permission guard - redirect unauthorized users
  useEffect(() => {
    if (!authLoading && !hasPermission('profilePage')) {
      navigate('/', { replace: true });
    }
  }, [authLoading, hasPermission, navigate]);

  // Show loading while checking permissions
  if (authLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render anything if user doesn't have permission (will redirect)
  if (!hasPermission('profilePage')) {
    return null;
  }

  const [isEditing, setIsEditing] = useState(false)
  const [activeTab, setActiveTab] = useState('personal')
  const [showSensitiveFields, setShowSensitiveFields] = useState(false)
  const { confirmPassword, confirmPasswordModal } = useConfirmPassword()
  const [expandedPayroll, setExpandedPayroll] = useState<string | null>(null)
  const [ptoModalOpen, setPtoModalOpen] = useState(false)
  const [eeapModalOpen, setEeapModalOpen] = useState(false)
  // Reviews disabled
  const [expandedBenefit, setExpandedBenefit] = useState<string | null>(null)

  // Real data state
  const [profile, setProfile] = useState<EmployeeProfile | null>(null)
  const [contact, setContact] = useState<ContactEntry | null>(null)
  const [certs, setCerts] = useState<EmployeeCert[]>([])
  const [certsLoading, setCertsLoading] = useState(false)

  const news = [
    'LTS wins new telecom contract',
    'Quarterly safety training announced',
    'Employee of the Month: Jane Doe',
  ]

  const recentActivity = [
    'User John submitted a new form',
    'Site 12 updated by Admin',
    'GIS data refreshed',
    'Resource "Safety Manual" added',
  ]

  // Load profile and contact for current signed-in employee only
  useEffect(() => {
    const id = user?.employeeId
    if (!id) return
    Promise.all([
      profileService.getMyProfile().catch(() => null),
      profileService.getContactByEmployeeId(id).catch(() => null)
    ]).then(([p, c]) => {
      setProfile(p)
      setContact(c)
    })
  }, [user?.employeeId])

  // Load certs for current signed-in employee only
  useEffect(() => {
    const id = user?.employeeId
    if (!id) return
    setCertsLoading(true)
    profileService.getCertsByEmployeeId(id).then(setCerts).catch(() => setCerts([])).finally(() => setCertsLoading(false))
  }, [user?.employeeId])

  // Mock employee data - in a real app this would come from an API
  const employeeData = {
    personal: {
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@lts.com',
      phone: '(555) 123-4567',
      address: '123 Main St, Anytown, ST 12345',
      dateOfBirth: '1985-03-15',
      emergencyContact: {
        name: 'Jane Doe',
        relationship: 'Spouse',
        phone: '(555) 987-6543'
      }
    },
    employment: {
      employeeId: 'LTS-2024-001',
      hireDate: '2020-06-15',
      position: 'Field Technician',
      department: 'Operations',
      supervisor: 'Mike Johnson',
      status: 'Active',
      payRate: '$25.50/hour',
      schedule: 'Monday-Friday, 7:00 AM - 3:30 PM'
    },
    benefits: {
      healthInsurance: {
        status: 'Active',
        provider: 'Select Health',
        providerUrl: 'https://selecthealth.org',
        cardDownloadUrl: '/api/benefits/health-card.pdf',
        employerContribution: '$450.00/month',
        employeeContribution: '$45.00/month'
      },
      dentalInsurance: {
        status: 'Active',
        provider: 'Ameritas',
        providerUrl: 'https://ameritas.com',
        cardDownloadUrl: '/api/benefits/dental-card.pdf',
        employerContribution: '$120.00/month',
        employeeContribution: '$15.00/month'
      },
      visionInsurance: {
        status: 'Active',
        provider: 'EMI',
        providerUrl: 'https://emivision.com',
        cardDownloadUrl: '/api/benefits/vision-card.pdf',
        employerContribution: '$45.00/month',
        employeeContribution: '$8.00/month'
      },
      hsa: {
        status: 'Active',
        provider: 'HealthEquity',
        providerUrl: 'https://healthequity.com',
        employerContribution: '$50.00/month',
        employeeContribution: 'Varies',
      },
      basicLife: {
        status: 'Active',
        provider: 'Lincoln',
        providerUrl: 'https://lincolnfinancial.com',
        coverage: '$50,000',
        employeeContribution: '$0.00/month'
      },
      voluntaryLifeAD: {
        status: 'Active',
        provider: 'Lincoln',
        providerUrl: 'https://lincolnfinancial.com',
        employeeContribution: '$12.50/month'
      },
      accident: {
        status: 'Active',
        provider: 'Assurity',
        providerUrl: 'https://assurity.com',
        employeeContribution: '$8.75/month'
      },
      hospitalIndemnity: {
        status: 'Active',
        provider: 'Assurity',
        providerUrl: 'https://assurity.com',
        employeeContribution: '$15.25/month'
      },
      identityProtection: {
        status: 'Active',
        provider: 'LifeLock',
        providerUrl: 'https://lifelock.com',
        employeeContribution: '$5.50/month'
      },
      petsBest: {
        status: 'Active',
        provider: 'Pets Best',
        providerUrl: 'https://www.petsbest.com',
        employerContribution: 'N/A',
        employeeContribution: '$18.00/month',
      },
      retirementPlan: {
        status: 'Active',
        provider: 'Fidelity',
        providerUrl: 'https://fidelity.com',
        type: 'Simple IRA',
        companyMatch: '3%'
      },
      ptoBalance: '120 hours'
    },
    training: [
      {
        name: 'Safety Training',
        completionDate: '2024-01-15',
        expiryDate: '2025-01-15',
        status: 'Current'
      },
      {
        name: 'OSHA 10-Hour',
        completionDate: '2023-08-20',
        expiryDate: '2026-08-20',
        status: 'Current'
      },
      {
        name: 'First Aid/CPR',
        completionDate: '2023-11-10',
        expiryDate: '2025-11-10',
        status: 'Current'
      }
    ],
    documents: {
      hrDocuments: [
        { name: 'W-4 Form', lastUpdated: '2024-01-15', status: 'Current', type: 'pdf', size: '245 KB' },
        { name: 'Direct Deposit Form', lastUpdated: '2024-01-15', status: 'Current', type: 'pdf', size: '189 KB' },
        { name: 'Employee Handbook Acknowledgment', lastUpdated: '2024-01-15', status: 'Current', type: 'pdf', size: '156 KB' },
        { name: 'Safety Policy Acknowledgment', lastUpdated: '2024-01-15', status: 'Current', type: 'pdf', size: '203 KB' }
      ],
      trainingCertificates: [
        { name: 'Safety Training Certificate', lastUpdated: '2024-01-15', status: 'Current', type: 'pdf', size: '1.2 MB' },
        { name: 'OSHA 10-Hour Certificate', lastUpdated: '2023-08-20', status: 'Current', type: 'pdf', size: '856 KB' },
        { name: 'First Aid CPR Certificate', lastUpdated: '2023-11-10', status: 'Current', type: 'pdf', size: '723 KB' }
      ],
      performanceReviews: [
        { name: 'Annual Review 2024', lastUpdated: '2024-01-15', status: 'Current', type: 'pdf', size: '445 KB' },
        { name: 'Mid-Year Review 2023', lastUpdated: '2023-07-15', status: 'Current', type: 'pdf', size: '389 KB' }
      ],
      payrollDocuments: [
        { name: 'Paystub January 2024', lastUpdated: '2024-01-15', status: 'Current', type: 'pdf', size: '234 KB' },
        { name: 'Paystub December 2023', lastUpdated: '2023-12-31', status: 'Current', type: 'pdf', size: '231 KB' },
        { name: 'W-2 Form 2023', lastUpdated: '2024-01-31', status: 'Current', type: 'pdf', size: '156 KB' }
      ]
    },
    payroll: [
      {
        id: 'pay-2024-01',
        payDate: '2024-01-15',
        payPeriod: '01/01/2024 - 01/15/2024',
        grossPay: 2040.00,
        netPay: 1652.40,
        hours: 80,
        rate: 25.50,
        deductions: {
          federalTax: 204.00,
          stateTax: 81.60,
          socialSecurity: 126.48,
          medicare: 29.58,
          healthInsurance: 45.00,
          dentalInsurance: 15.00,
          visionInsurance: 8.00,
          simpleIRA: 61.20
        }
      },
      {
        id: 'pay-2023-12',
        payDate: '2023-12-31',
        payPeriod: '12/16/2023 - 12/31/2023',
        grossPay: 2040.00,
        netPay: 1652.40,
        hours: 80,
        rate: 25.50,
        deductions: {
          federalTax: 204.00,
          stateTax: 81.60,
          socialSecurity: 126.48,
          medicare: 29.58,
          healthInsurance: 45.00,
          dentalInsurance: 15.00,
          visionInsurance: 8.00,
          simpleIRA: 61.20
        }
      },
      {
        id: 'pay-2023-11',
        payDate: '2023-11-30',
        payPeriod: '11/16/2023 - 11/30/2023',
        grossPay: 2040.00,
        netPay: 1652.40,
        hours: 80,
        rate: 25.50,
        deductions: {
          federalTax: 204.00,
          stateTax: 81.60,
          socialSecurity: 126.48,
          medicare: 29.58,
          healthInsurance: 45.00,
          dentalInsurance: 15.00,
          visionInsurance: 8.00,
          simpleIRA: 61.20
        }
      }
    ],
    reviews: [
      {
        id: 'review-2024-01',
        reviewDate: '2024-01-15',
        reviewType: 'Annual Review',
        reviewer: 'Mike Johnson',
        rating: 4.5,
        status: 'Completed',
        comments: 'Excellent performance throughout the year. Strong technical skills and great team collaboration. Recommended for continued growth and development.',
        goals: [
          'Complete advanced safety training certification',
          'Take on additional project leadership responsibilities',
          'Mentor junior technicians'
        ]
      },
      {
        id: 'review-2023-07',
        reviewDate: '2023-07-15',
        reviewType: 'Mid-Year Review',
        reviewer: 'Mike Johnson',
        rating: 4.0,
        status: 'Completed',
        comments: 'Good progress on assigned projects. Shows initiative and willingness to learn new technologies. Areas for improvement in documentation.',
        goals: [
          'Improve technical documentation skills',
          'Complete intermediate safety training',
          'Increase efficiency in routine tasks'
        ]
      }
    ]
  }

  const benefitList = [
    {
      key: 'healthInsurance',
      label: 'Health Insurance',
      icon: faShieldAlt,
    },
    {
      key: 'dentalInsurance',
      label: 'Dental Insurance',
      icon: faShieldAlt,
    },
    {
      key: 'visionInsurance',
      label: 'Vision Insurance',
      icon: faShieldAlt,
    },
    {
      key: 'hsa',
      label: 'Health Savings Account (HSA)',
      icon: faShieldAlt,
    },
    {
      key: 'basicLife',
      label: 'Basic Life Insurance',
      icon: faShieldAlt,
    },
    {
      key: 'voluntaryLifeAD',
      label: 'Voluntary Life AD&D',
      icon: faShieldAlt,
    },
    {
      key: 'accident',
      label: 'Accident Insurance',
      icon: faShieldAlt,
    },
    {
      key: 'hospitalIndemnity',
      label: 'Hospital Indemnity',
      icon: faShieldAlt,
    },
    {
      key: 'identityProtection',
      label: 'Identity Protection',
      icon: faShieldAlt,
    },
    {
      key: 'petsBest',
      label: 'Pets Best Pet Insurance',
      icon: faShieldAlt,
    },
  ]

  const additionalBenefits = [
    {
      name: 'Employee Equipment Assistance Program (EEAP)',
      description: 'Request tools and equipment for work.',
      action: {
        label: 'Access EEAP',
        onClick: () => setEeapModalOpen(true),
      },
    },
    {
      name: 'Zero-Cost Virtual Visits for Primary Care and Mental Health',
      description: 'Many plans offer $0 cost (before deductible) for virtual video visits with in-network primary care providers (PCPs) and mental health providers, accessible via the Intermountain Health Patient Portal app or other platforms.',
      badge: 'Medical Enrollment Required',
      action: {
        label: 'Learn More',
        href: '#',
      },
    },
    {
      name: 'Intermountain Employee Assistance Program (IEAP)',
      description: 'Provides up to four free counseling sessions per incident for employees and family members (ages 6–26), 24/7 crisis response, elder care support, and web-based resources. Call 800-832-7733 to access.',
      badge: 'Medical Enrollment Required',
      action: {
        label: 'Learn More',
        href: '#',
      },
    },
    {
      name: 'Wellness Rewards Program (Up to $2,400/Year)',
      description: 'Earn up to $2,000 per family annually for activities like gym memberships, acupuncture, Tai Chi, yoga, or physical activity tracked via the Sworkit app, with reimbursements up to $800 monthly.',
      badge: 'Medical Enrollment Required',
      action: {
        label: 'Learn More',
        href: '#',
      },
    },
    {
      name: '100% Covered Preventive Care',
      description: 'Covers annual physicals, screenings (e.g., mammograms, colonoscopies), immunizations, contraception, and breastfeeding supplies at no copay, coinsurance, or deductible when billed with preventive codes.',
      badge: 'Medical Enrollment Required',
      action: {
        label: 'Learn More',
        href: '#',
      },
    },
    {
      name: 'Intermountain Connect Care (Virtual Care)',
      description: '24/7 virtual visits for common illnesses via smartphone, computer, or tablet at low or no out-of-pocket cost, accessible at IntermountainConnectCare.org.',
      badge: 'Medical Enrollment Required',
      action: {
        label: 'Learn More',
        href: '#',
      },
    },
    {
      name: 'Member Discounts Program',
      description: 'Offers discounts on LASIK surgery, gym memberships, hearing aids, cosmetic dermatology, eyewear, and more, accessible via SelectHealth.org/discounts.',
      badge: 'Medical Enrollment Required',
      action: {
        label: 'Learn More',
        href: '#',
      },
    },
  ];

  const handleSave = async () => {
    try {
      const targetId = profile?.employeeId || user?.employeeId
      if (!targetId) throw new Error('No employee selected')
      // Self-service: preferredName + personal contact only
      await profileService.updateMyContact({
        preferredName: contact?.preferredName ?? undefined,
        email: contact?.email || undefined,
        phone: contact?.phone || undefined,
      })
      // Optional: also sync preferredName to profile record
      const profileUpdates: { preferredName?: string } = {}
      const newPreferred = contact?.preferredName ?? undefined
      const currentPreferred = profile?.preferredName ?? undefined
      if (newPreferred !== undefined && newPreferred !== currentPreferred) {
        profileUpdates.preferredName = newPreferred
      }
      if (Object.keys(profileUpdates).length > 0) {
        await profileService.updateProfileByEmployeeId(targetId, profileUpdates)
      }
      setIsEditing(false)
    } catch (e) {
      // keep editing if failed; in future: surface toast
      console.error(e)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const canEditCurrentView = !!user?.employeeId

  const formatDateDisplay = (value?: string | null) => {
    if (!value) return 'Not set'
    const raw = String(value).trim()
    const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch
      return `${month}/${day}/${year}`
    }
    const parsed = new Date(raw)
    if (Number.isNaN(parsed.getTime())) return raw
    return parsed.toLocaleDateString()
  }

  const formatDriverStatusDisplay = (value?: string | null) => {
    const normalized = String(value || '').trim().toLowerCase()
    if (!normalized) return 'Not set'
    if (normalized === 'valid' || normalized === 'good') return 'Valid'
    if (normalized === 'invalid' || normalized === 'bad') return 'Invalid'
    if (normalized === 'pending' || normalized === 'waiting' || normalized === 'in-progress' || normalized === 'inprogress') return 'Pending'
    return normalized.charAt(0).toUpperCase() + normalized.slice(1)
  }

  const formatLicenseTypeDisplay = (value?: string | null) => {
    const normalized = String(value || '').trim().toLowerCase()
    if (!normalized) return 'Not set'
    if (normalized === 'cdl') return 'CDL'
    if (normalized === 'non-cdl' || normalized === 'non cdl' || normalized === 'standard' || normalized === 'class d' || normalized === 'regular') return 'Non-CDL'
    if (normalized === 'none' || normalized === 'n/a' || normalized === 'na') return 'None'
    return normalized.toUpperCase()
  }

  const formatPayTypeDisplay = (value?: string | null) => {
    const normalized = String(value || '').trim().toLowerCase()
    if (!normalized) return 'Not set'
    if (normalized === 'salary' || normalized === 'salaried') return 'Salary'
    if (normalized === 'hourly' || normalized === 'hour') return 'Hourly'
    return value as string
  }

  const maskedSensitiveValue = (visibleValue: string) => (showSensitiveFields ? visibleValue : 'Hidden')

  const clearSensitiveFromProfile = () => {
    setProfile((prev) => {
      if (!prev) return prev
      const next = { ...prev }
      delete next.hireDate
      delete next.driverStatus
      delete next.driversLicenseExpirationDate
      delete next.driversLicenseType
      delete next.dotMedicalCardExpirationDate
      delete next.zipCode
      delete next.payType
      return next
    })
  }

  const handleSensitiveToggle = async (checked: boolean) => {
    if (!checked) {
      setShowSensitiveFields(false)
      clearSensitiveFromProfile()
      return
    }
    const unlocked = await confirmPassword({
      title: 'Confirm your password',
      description: 'Re-enter your account password to view hire date, pay type, home ZIP code, driver license, and DOT medical card details.',
      confirmLabel: 'Unlock',
      onConfirm: async (currentPassword) => {
        const sensitive = await profileService.unlockSensitiveSelf(currentPassword)
        setProfile((prev) => (prev ? { ...prev, ...sensitive } : prev))
        return true
      },
    })
    if (unlocked) setShowSensitiveFields(true)
  }

  const sensitiveFieldsToggle = (
    <label className="label cursor-pointer gap-3 py-0">
      <span className="label-text text-sm font-medium">Show sensitive fields</span>
      <input
        type="checkbox"
        className="toggle toggle-primary"
        checked={showSensitiveFields}
        onChange={(e) => handleSensitiveToggle(e.target.checked)}
      />
    </label>
  )

  const renderPersonalInfo = () => (
    <div className="card bg-base-100 shadow-lg">
      <div className="card-body">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="card-title flex items-center gap-2">
            <FontAwesomeIcon icon={faUser} />
            Personal Information
          </h3>
          <div className="flex flex-wrap items-center gap-3">
            {sensitiveFieldsToggle}
            {!isEditing && canEditCurrentView && (
              <button
                className="btn btn-sm btn-primary"
                onClick={() => setIsEditing(true)}
              >
                <FontAwesomeIcon icon={faEdit} className="mr-2" />
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="label">
                <span className="label-text font-semibold">First Name</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={(contact?.name || '').split(' ')[0] || ''}
                disabled
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold">Last Name</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={(contact?.name || '').split(' ').slice(1).join(' ') || ''}
                disabled
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold">Preferred Name</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={contact?.preferredName || ''}
                onChange={e => setContact(c => c ? { ...c, preferredName: e.target.value } : c)}
                disabled={!isEditing}
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold flex items-center gap-2">
                  <FontAwesomeIcon icon={faEnvelope} />
                  Personal email
                </span>
              </label>
              <input
                type="email"
                className="input input-bordered w-full"
                value={contact?.email || ''}
                onChange={e => setContact(c => c ? { ...c, email: e.target.value } : c)}
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="label">
                <span className="label-text font-semibold flex items-center gap-2">
                  <FontAwesomeIcon icon={faPhone} />
                  Personal phone
                </span>
              </label>
              <input
                type="tel"
                className="input input-bordered w-full"
                value={contact?.phone || ''}
                onChange={e => setContact(c => c ? { ...c, phone: e.target.value } : c)}
                disabled={!isEditing}
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold flex items-center gap-2">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  Home ZIP code
                </span>
              </label>
              {showSensitiveFields ? (
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value={profile?.zipCode || 'Not set'}
                  disabled
                  readOnly
                  aria-label="Home ZIP code"
                />
              ) : (
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value="Hidden"
                  disabled
                  readOnly
                  aria-label="Home ZIP code hidden"
                />
              )}
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold flex items-center gap-2">
                  <FontAwesomeIcon icon={faEnvelope} />
                  Work email
                </span>
              </label>
              <input
                type="email"
                className="input input-bordered w-full"
                value={contact?.workEmail ?? ''}
                disabled
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold flex items-center gap-2">
                  <FontAwesomeIcon icon={faPhone} />
                  Work phone
                </span>
              </label>
              <input
                type="tel"
                className="input input-bordered w-full"
                value={contact?.workPhone ?? ''}
                disabled
              />
            </div>
          </div>
        </div>

        {isEditing && canEditCurrentView && (
          <div className="flex justify-end gap-2 mt-6">
            <button className="btn btn-ghost" onClick={handleCancel}>
              <FontAwesomeIcon icon={faTimes} className="mr-2" />
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              Save Changes
            </button>
          </div>
        )}
      </div>
    </div>
  )

  const renderEmploymentInfo = () => (
    <div className="card bg-base-100 shadow-lg">
      <div className="card-body">
        <div className="flex items-center justify-between mb-4">
          <h3 className="card-title flex items-center gap-2">
            <FontAwesomeIcon icon={faBriefcase} />
            Employment Information
          </h3>
          {sensitiveFieldsToggle}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="label">
                <span className="label-text font-semibold flex items-center gap-2">
                  <FontAwesomeIcon icon={faIdCard} />
                  Employee ID
                </span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={profile?.employeeId || ''}
                disabled
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold">Hire Date</span>
              </label>
              {showSensitiveFields ? (
                <DaisyDatePicker
                  className="w-full"
                  value={(profile?.hireDate || '').slice(0, 10)}
                  onChange={() => {}}
                  disabled
                  aria-label="Hire date"
                />
              ) : (
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value="Hidden"
                  disabled
                />
              )}
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold">Pay Type</span>
              </label>
              {showSensitiveFields ? (
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value={formatPayTypeDisplay(profile?.payType)}
                  disabled
                  readOnly
                  aria-label="Pay type"
                />
              ) : (
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value="Hidden"
                  disabled
                  readOnly
                  aria-label="Pay type"
                />
              )}
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold flex items-center gap-2">
                  <FontAwesomeIcon icon={faUserTie} />
                  Position
                </span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={contact?.position || ''}
                disabled
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold flex items-center gap-2">
                  <FontAwesomeIcon icon={faBuilding} />
                  Department
                </span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={contact?.department || ''}
                disabled
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="label">
                <span className="label-text font-semibold">Supervisor</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={contact?.supervisor || ''}
                disabled
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold">Driver Status</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={maskedSensitiveValue(formatDriverStatusDisplay(profile?.driverStatus))}
                disabled
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold">Driver License Expiration</span>
              </label>
              {showSensitiveFields ? (
                <DaisyDatePicker
                  className="w-full"
                  value={(profile?.driversLicenseExpirationDate || '').slice(0, 10)}
                  onChange={() => {}}
                  disabled
                  aria-label="Driver license expiration"
                />
              ) : (
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value="Hidden"
                  disabled
                />
              )}
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold">Driver License Type</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={maskedSensitiveValue(formatLicenseTypeDisplay(profile?.driversLicenseType))}
                disabled
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-semibold">DOT Medical Card Expiration</span>
              </label>
              {showSensitiveFields ? (
                <DaisyDatePicker
                  className="w-full"
                  value={(profile?.dotMedicalCardExpirationDate || '').slice(0, 10)}
                  onChange={() => {}}
                  disabled
                  aria-label="DOT medical card expiration"
                />
              ) : (
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value="Hidden"
                  disabled
                />
              )}
            </div>
            {/* Pay Rate hidden for HIPAA compliance */}
          </div>
        </div>
      </div>
    </div>
  )

  const renderEmployeeCerts = () => {
    const empId = user?.employeeId
    const handleViewCert = async (cert: EmployeeCert) => {
      if (!empId) return
      const title = certDisplayLabel(cert)
      if (!cert.filePath) {
        openDocument(title, 'PDF', null, 'No document uploaded for this certificate.')
        return
      }
      openDocument(title, 'PDF')
      try {
        const blobUrl = await profileService.getCertFileBlobUrl(empId, cert.type)
        openDocument(title, 'PDF', blobUrl)
      } catch {
        openDocument(title, 'PDF', null, 'Failed to load certificate document.')
      }
    }
    const statusBadgeClass = (s: string) => (s === 'good' ? 'badge-success' : s === 'bad' ? 'badge-error' : 'badge-warning')
    const formatDate = (d: string | null) => formatDateDisplay(d)
    return (
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <h3 className="card-title flex items-center gap-2 mb-4">
            <FontAwesomeIcon icon={faGraduationCap} />
            Employee Certifications
          </h3>
          {certsLoading ? (
            <div className="flex justify-center py-8">
              <FontAwesomeIcon icon={faSpinner} className="text-2xl text-primary animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Certification</th>
                    <th>Expiration Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {certs.map((cert) => (
                    <tr key={cert.type}>
                      <td className="font-medium">{certDisplayLabel(cert)}</td>
                      <td>{formatDate(cert.expirationDate)}</td>
                      <td>
                        <span className={`badge ${statusBadgeClass(cert.status)}`}>
                          {cert.status === 'good' ? 'Complete' : cert.status === 'bad' ? 'Expired' : 'In Progress'}
                        </span>
                      </td>
                      <td className="flex flex-wrap gap-2">
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => handleViewCert(cert)}
                          title={cert.filePath ? 'View certificate' : 'No document uploaded'}
                        >
                          <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderBenefits = () => (
    <div className="card bg-base-100 shadow-lg">
      <div className="card-body">
        <h3 className="card-title flex items-center gap-2 mb-4">
          <FontAwesomeIcon icon={faShieldAlt} />
          Benefits & Time Off
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-lg">Insurance Benefits</h4>
            <div className="space-y-4">
              {benefitList
                .filter((benefit) => typeof employeeData.benefits[benefit.key as keyof typeof employeeData.benefits] === 'object')
                .map((benefit) => {
                  const data = employeeData.benefits[benefit.key as keyof typeof employeeData.benefits] as Record<string, any>;
                  const isExpanded = expandedBenefit === benefit.key;
                  return (
                    <div
                      key={benefit.key}
                      className={`card bg-base-200 shadow-sm transition-all duration-200 ${isExpanded ? 'ring-2 ring-primary' : ''}`}
                    >
                      <div
                        className="flex items-center justify-between px-4 py-3 cursor-pointer gap-2"
                        onClick={() => setExpandedBenefit(isExpanded ? null : benefit.key)}
                        style={{ minHeight: '56px' }}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FontAwesomeIcon icon={benefit.icon} className="text-lg shrink-0" />
                          <span className="font-semibold truncate">{benefit.label}</span>
                          <span className="text-xs text-base-content/60 truncate ml-2">{data.provider}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="badge badge-success badge-sm shrink-0">{data.status}</span>
                          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="text-base-content/60 text-lg ml-1" />
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-2 bg-base-300/40 border-t border-base-200">
                          {/* Details for each benefit */}
                          {benefit.key === 'healthInsurance' && (
                            <>
                              <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                                <div>
                                  <span className="text-base-content/60">Employer Contribution:</span>
                                  <p className="font-medium text-success">{data.employerContribution}</p>
                                </div>
                                <div>
                                  <span className="text-base-content/60">Your Contribution:</span>
                                  <p className="font-medium">{data.employeeContribution}</p>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a href={data.providerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline btn-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                                  Visit Provider
                                </a>
                                <button className="btn btn-sm btn-outline btn-info" onClick={e => { e.stopPropagation(); openDocument(`${benefit.label} Benefit Breakdown`, 'PDF'); }}>
                                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                                  Benefit Breakdown
                                </button>
                                <a href={data.cardDownloadUrl} className="btn btn-sm btn-outline btn-secondary" download>
                                  <FontAwesomeIcon icon={faDownload} className="mr-1" />
                                  Download Card
                                </a>
                              </div>
                            </>
                          )}
                          {benefit.key === 'dentalInsurance' && (
                            <>
                              <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                                <div>
                                  <span className="text-base-content/60">Employer Contribution:</span>
                                  <p className="font-medium text-success">{data.employerContribution}</p>
                                </div>
                                <div>
                                  <span className="text-base-content/60">Your Contribution:</span>
                                  <p className="font-medium">{data.employeeContribution}</p>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a href={data.providerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline btn-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                                  Visit Provider
                                </a>
                                <button className="btn btn-sm btn-outline btn-info" onClick={e => { e.stopPropagation(); openDocument(`${benefit.label} Benefit Breakdown`, 'PDF'); }}>
                                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                                  Benefit Breakdown
                                </button>
                                <a href={data.cardDownloadUrl} className="btn btn-sm btn-outline btn-secondary" download>
                                  <FontAwesomeIcon icon={faDownload} className="mr-1" />
                                  Download Card
                                </a>
                              </div>
                            </>
                          )}
                          {benefit.key === 'visionInsurance' && (
                            <>
                              <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                                <div>
                                  <span className="text-base-content/60">Employer Contribution:</span>
                                  <p className="font-medium text-success">{data.employerContribution}</p>
                                </div>
                                <div>
                                  <span className="text-base-content/60">Your Contribution:</span>
                                  <p className="font-medium">{data.employeeContribution}</p>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a href={data.providerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline btn-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                                  Visit Provider
                                </a>
                                <button className="btn btn-sm btn-outline btn-info" onClick={e => { e.stopPropagation(); openDocument(`${benefit.label} Benefit Breakdown`, 'PDF'); }}>
                                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                                  Benefit Breakdown
                                </button>
                                <a href={data.cardDownloadUrl} className="btn btn-sm btn-outline btn-secondary" download>
                                  <FontAwesomeIcon icon={faDownload} className="mr-1" />
                                  Download Card
                                </a>
                              </div>
                            </>
                          )}
                          {benefit.key === 'basicLife' && (
                            <>
                              <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                                <div>
                                  <span className="text-base-content/60">Coverage:</span>
                                  <p className="font-medium">{data.coverage}</p>
                                </div>
                                <div>
                                  <span className="text-base-content/60">Your Contribution:</span>
                                  <p className="font-medium">{data.employeeContribution}</p>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a href={data.providerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline btn-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                                  Visit Provider
                                </a>
                                <button className="btn btn-sm btn-outline btn-info" onClick={e => { e.stopPropagation(); openDocument(`${benefit.label} Benefit Breakdown`, 'PDF'); }}>
                                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                                  Benefit Breakdown
                                </button>
                              </div>
                            </>
                          )}
                          {benefit.key === 'voluntaryLifeAD' && (
                            <>
                              <div className="mb-3 text-sm">
                                <span className="text-base-content/60">Your Contribution:</span>
                                <p className="font-medium">{data.employeeContribution}</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a href={data.providerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline btn-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                                  Visit Provider
                                </a>
                                <button className="btn btn-sm btn-outline btn-info" onClick={e => { e.stopPropagation(); openDocument(`${benefit.label} Benefit Breakdown`, 'PDF'); }}>
                                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                                  Benefit Breakdown
                                </button>
                              </div>
                            </>
                          )}
                          {benefit.key === 'accident' && (
                            <>
                              <div className="mb-3 text-sm">
                                <span className="text-base-content/60">Your Contribution:</span>
                                <p className="font-medium">{data.employeeContribution}</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a href={data.providerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline btn-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                                  Visit Provider
                                </a>
                                <button className="btn btn-sm btn-outline btn-info" onClick={e => { e.stopPropagation(); openDocument(`${benefit.label} Benefit Breakdown`, 'PDF'); }}>
                                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                                  Benefit Breakdown
                                </button>
                              </div>
                            </>
                          )}
                          {benefit.key === 'hospitalIndemnity' && (
                            <>
                              <div className="mb-3 text-sm">
                                <span className="text-base-content/60">Your Contribution:</span>
                                <p className="font-medium">{data.employeeContribution}</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a href={data.providerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline btn-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                                  Visit Provider
                                </a>
                                <button className="btn btn-sm btn-outline btn-info" onClick={e => { e.stopPropagation(); openDocument(`${benefit.label} Benefit Breakdown`, 'PDF'); }}>
                                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                                  Benefit Breakdown
                                </button>
                              </div>
                            </>
                          )}
                          {benefit.key === 'identityProtection' && (
                            <>
                              <div className="mb-3 text-sm">
                                <span className="text-base-content/60">Your Contribution:</span>
                                <p className="font-medium">{data.employeeContribution}</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a href={data.providerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline btn-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                                  Visit Provider
                                </a>
                                <button className="btn btn-sm btn-outline btn-info" onClick={e => { e.stopPropagation(); openDocument(`${benefit.label} Benefit Breakdown`, 'PDF'); }}>
                                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                                  Benefit Breakdown
                                </button>
                              </div>
                            </>
                          )}
                          {benefit.key === 'hsa' && (
                            <>
                              <div className="mb-3 text-sm">
                                <span className="text-base-content/60">Your Contribution:</span>
                                <p className="font-medium">{data.employeeContribution}</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a href={data.providerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline btn-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                                  Visit Provider
                                </a>
                                <button className="btn btn-sm btn-outline btn-info" onClick={e => { e.stopPropagation(); openDocument(`${benefit.label} Benefit Breakdown`, 'PDF'); }}>
                                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                                  Benefit Breakdown
                                </button>
                              </div>
                            </>
                          )}
                          {benefit.key === 'petsBest' && (
                            <>
                              <div className="mb-3 text-sm">
                                <span className="text-base-content/60">Your Contribution:</span>
                                <p className="font-medium">{data.employeeContribution}</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a href={data.providerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline btn-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                                  Visit Provider
                                </a>
                                <button className="btn btn-sm btn-outline btn-info" onClick={e => { e.stopPropagation(); openDocument(`${benefit.label} Benefit Breakdown`, 'PDF'); }}>
                                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                                  Benefit Breakdown
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
          
          <div className="space-y-4">
            <h4 className="font-semibold text-lg">Time Off & Retirement</h4>
            <div className="space-y-4">
              {/* PTO Balance */}
              <div className="card bg-base-200 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <h5 className="font-semibold">PTO Balance</h5>
                      <div className="tooltip" data-tip="PTO (Paid Time Off) includes vacation, sick leave, and personal time in one combined balance">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-base-content/60 text-sm cursor-help" />
                      </div>
                    </div>
                    <span className="text-info font-semibold text-lg">{employeeData.benefits.ptoBalance}</span>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      className="btn btn-sm btn-outline btn-primary"
                      onClick={() => setPtoModalOpen(true)}
                    >
                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                      Request PTO
                    </button>
                  </div>
                </div>
              </div>

              {/* Retirement Plan */}
              <div className="card bg-base-200 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h5 className="font-semibold">{employeeData.benefits.retirementPlan.type}</h5>
                      <p className="text-sm text-base-content/70">{employeeData.benefits.retirementPlan.provider}</p>
                    </div>
                    <span className="badge badge-success">{employeeData.benefits.retirementPlan.status}</span>
                  </div>
                  <div className="mb-3 text-sm">
                    <span className="text-base-content/60">Company Match:</span>
                    <p className="font-medium text-success">{employeeData.benefits.retirementPlan.companyMatch}</p>
                  </div>
                  <div className="flex gap-2">
                    <a 
                      href={employeeData.benefits.retirementPlan.providerUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline btn-primary"
                    >
                      <FontAwesomeIcon icon={faGlobe} className="mr-1" />
                      Visit Website
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 mt-8">
          <h4 className="font-semibold text-lg">Additional Benefits</h4>
          <div className="space-y-3">
            {additionalBenefits.map((benefit, idx) => (
              <div key={idx} className="card bg-base-200 shadow-sm p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div className="flex-1">
                  <div className="font-semibold">{benefit.name}</div>
                  <div className="text-sm text-base-content/70">{benefit.description}</div>
                </div>
                <div className="flex items-center gap-2">
                  {benefit.badge && (
                    <span className="badge badge-warning badge-outline text-xs font-semibold">{benefit.badge}</span>
                  )}
                  {benefit.action && (
                    benefit.action.onClick ? (
                      <button className="btn btn-sm btn-outline btn-primary mt-2 md:mt-0" onClick={benefit.action.onClick}>{benefit.action.label}</button>
                    ) : (
                      benefit.badge === 'Medical Enrollment Required' ? (
                        <button className="btn btn-sm btn-outline btn-primary mt-2 md:mt-0" onClick={() => openDocument(benefit.name, 'PDF')}>{benefit.action.label}</button>
                      ) : (
                        <a className="btn btn-sm btn-outline btn-primary mt-2 md:mt-0" href={benefit.action.href} target="_blank" rel="noopener noreferrer">{benefit.action.label}</a>
                      )
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  const renderTraining = () => (
    <div className="card bg-base-100 shadow-lg">
      <div className="card-body">
        <h3 className="card-title flex items-center gap-2 mb-4">
          <FontAwesomeIcon icon={faGraduationCap} />
          Training & Certifications
        </h3>
        
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Training/Certification</th>
                <th>Completion Date</th>
                <th>Expiry Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {employeeData.training.map((training, index) => (
                <tr key={index}>
                  <td className="font-medium">{training.name}</td>
                  <td>{training.completionDate}</td>
                  <td>{training.expiryDate}</td>
                  <td>
                    <span className="badge badge-success">{training.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderDocuments = () => (
    <div className="card bg-base-100 shadow-lg">
      <div className="card-body">
        <h3 className="card-title flex items-center gap-2 mb-4">
          <FontAwesomeIcon icon={faFileAlt} />
          Document Explorer
        </h3>
        
        <div>
          <h4 className="font-semibold mb-3">Employee Documents</h4>
          <ul className="menu menu-sm bg-base-200 rounded-box w-full">
            {/* HR Documents */}
            <li>
              <details open>
                <summary>
                  <FontAwesomeIcon icon={faFolderOpen} className="h-4 w-4" />
                  HR Documents
                </summary>
                <ul>
                  <li>
                    <details>
                      <summary>
                        <FontAwesomeIcon icon={faFolder} className="h-4 w-4" />
                        Employment Forms
                      </summary>
                      <ul>
                        {employeeData.documents.hrDocuments.slice(0, 2).map((doc, index) => (
                          <li key={index}>
                            <button 
                              className="flex items-center gap-2 w-full text-left"
                              onClick={() => openDocument(doc.name, 'PDF')}
                            >
                              <FontAwesomeIcon icon={faFilePdf} className="h-4 w-4 text-red-500" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{doc.name}</div>
                                <div className="text-xs text-base-content/60">
                                  {doc.lastUpdated} • {doc.size}
                                </div>
                              </div>
                              <span className="badge badge-success badge-xs">{doc.status}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                  <li>
                    <details>
                      <summary>
                        <FontAwesomeIcon icon={faFolder} className="h-4 w-4" />
                        Policy Acknowledgments
                      </summary>
                      <ul>
                        {employeeData.documents.hrDocuments.slice(2).map((doc, index) => (
                          <li key={index}>
                            <button 
                              className="flex items-center gap-2 w-full text-left"
                              onClick={() => openDocument(doc.name, 'PDF')}
                            >
                              <FontAwesomeIcon icon={faFilePdf} className="h-4 w-4 text-red-500" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{doc.name}</div>
                                <div className="text-xs text-base-content/60">
                                  {doc.lastUpdated} • {doc.size}
                                </div>
                              </div>
                              <span className="badge badge-success badge-xs">{doc.status}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                </ul>
              </details>
            </li>

            {/* Training Certificates */}
            <li>
              <details>
                <summary>
                  <FontAwesomeIcon icon={faFolderOpen} className="h-4 w-4" />
                  Training Certificates
                </summary>
                <ul>
                  <li>
                    <details>
                      <summary>
                        <FontAwesomeIcon icon={faFolder} className="h-4 w-4" />
                        Safety Training
                      </summary>
                      <ul>
                        {employeeData.documents.trainingCertificates.slice(0, 1).map((doc, index) => (
                          <li key={index}>
                            <button 
                              className="flex items-center gap-2 w-full text-left"
                              onClick={() => openDocument(doc.name, 'PDF')}
                            >
                              <FontAwesomeIcon icon={faFilePdf} className="h-4 w-4 text-red-500" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{doc.name}</div>
                                <div className="text-xs text-base-content/60">
                                  {doc.lastUpdated} • {doc.size}
                                </div>
                              </div>
                              <span className="badge badge-success badge-xs">{doc.status}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                  <li>
                    <details>
                      <summary>
                        <FontAwesomeIcon icon={faFolder} className="h-4 w-4" />
                        Professional Certifications
                      </summary>
                      <ul>
                        {employeeData.documents.trainingCertificates.slice(1).map((doc, index) => (
                          <li key={index}>
                            <button 
                              className="flex items-center gap-2 w-full text-left"
                              onClick={() => openDocument(doc.name, 'PDF')}
                            >
                              <FontAwesomeIcon icon={faFilePdf} className="h-4 w-4 text-red-500" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{doc.name}</div>
                                <div className="text-xs text-base-content/60">
                                  {doc.lastUpdated} • {doc.size}
                                </div>
                              </div>
                              <span className="badge badge-success badge-xs">{doc.status}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                </ul>
              </details>
            </li>

            {/* Performance Reviews */}
            <li>
              <details>
                <summary>
                  <FontAwesomeIcon icon={faFolderOpen} className="h-4 w-4" />
                  Performance Reviews
                </summary>
                <ul>
                  <li>
                    <details>
                      <summary>
                        <FontAwesomeIcon icon={faFolder} className="h-4 w-4" />
                        2024 Reviews
                      </summary>
                      <ul>
                        {employeeData.documents.performanceReviews.slice(0, 1).map((doc, index) => (
                          <li key={index}>
                            <button 
                              className="flex items-center gap-2 w-full text-left"
                              onClick={() => openDocument(doc.name, 'PDF')}
                            >
                              <FontAwesomeIcon icon={faFilePdf} className="h-4 w-4 text-red-500" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{doc.name}</div>
                                <div className="text-xs text-base-content/60">
                                  {doc.lastUpdated} • {doc.size}
                                </div>
                              </div>
                              <span className="badge badge-success badge-xs">{doc.status}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                  <li>
                    <details>
                      <summary>
                        <FontAwesomeIcon icon={faFolder} className="h-4 w-4" />
                        2023 Reviews
                      </summary>
                      <ul>
                        {employeeData.documents.performanceReviews.slice(1).map((doc, index) => (
                          <li key={index}>
                            <button 
                              className="flex items-center gap-2 w-full text-left"
                              onClick={() => openDocument(doc.name, 'PDF')}
                            >
                              <FontAwesomeIcon icon={faFilePdf} className="h-4 w-4 text-red-500" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{doc.name}</div>
                                <div className="text-xs text-base-content/60">
                                  {doc.lastUpdated} • {doc.size}
                                </div>
                              </div>
                              <span className="badge badge-success badge-xs">{doc.status}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                </ul>
              </details>
            </li>

            {/* Payroll Documents */}
            <li>
              <details>
                <summary>
                  <FontAwesomeIcon icon={faFolderOpen} className="h-4 w-4" />
                  Payroll Documents
                </summary>
                <ul>
                  <li>
                    <details>
                      <summary>
                        <FontAwesomeIcon icon={faFolder} className="h-4 w-4" />
                        2024 Paystubs
                      </summary>
                      <ul>
                        {employeeData.documents.payrollDocuments.slice(0, 1).map((doc, index) => (
                          <li key={index}>
                            <button 
                              className="flex items-center gap-2 w-full text-left"
                              onClick={() => openDocument(doc.name, 'PDF')}
                            >
                              <FontAwesomeIcon icon={faFilePdf} className="h-4 w-4 text-red-500" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{doc.name}</div>
                                <div className="text-xs text-base-content/60">
                                  {doc.lastUpdated} • {doc.size}
                                </div>
                              </div>
                              <span className="badge badge-success badge-xs">{doc.status}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                  <li>
                    <details>
                      <summary>
                        <FontAwesomeIcon icon={faFolder} className="h-4 w-4" />
                        2023 Documents
                      </summary>
                      <ul>
                        {employeeData.documents.payrollDocuments.slice(1).map((doc, index) => (
                          <li key={index}>
                            <button 
                              className="flex items-center gap-2 w-full text-left"
                              onClick={() => openDocument(doc.name, 'PDF')}
                            >
                              <FontAwesomeIcon icon={faFilePdf} className="h-4 w-4 text-red-500" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{doc.name}</div>
                                <div className="text-xs text-base-content/60">
                                  {doc.lastUpdated} • {doc.size}
                                </div>
                              </div>
                              <span className="badge badge-success badge-xs">{doc.status}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                </ul>
              </details>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )

  const renderPayroll = () => (
    <div className="card bg-base-100 shadow-lg">
      <div className="card-body">
        <h3 className="card-title flex items-center gap-2 mb-4">
          <FontAwesomeIcon icon={faDollarSign} />
          Payroll History
        </h3>
        
        <div className="space-y-4">
          {employeeData.payroll.map((paystub) => (
            <div key={paystub.id} className="border border-base-300 rounded-lg">
              {/* Paystub Header */}
              <div 
                className="p-4 cursor-pointer hover:bg-base-200 transition-colors"
                onClick={() => setExpandedPayroll(expandedPayroll === paystub.id ? null : paystub.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <FontAwesomeIcon 
                      icon={expandedPayroll === paystub.id ? faChevronDown : faChevronRight} 
                      className="text-base-content/60"
                    />
                    <div>
                      <h4 className="font-semibold">Pay Date: {paystub.payDate}</h4>
                      <p className="text-sm text-base-content/60">{paystub.payPeriod}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">${paystub.netPay.toFixed(2)}</div>
                    <div className="text-sm text-base-content/60">Net Pay</div>
                  </div>
                </div>
              </div>

              {/* Expanded Paystub Details */}
              {expandedPayroll === paystub.id && (
                <div className="border-t border-base-300 p-4 bg-base-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Earnings */}
                    <div>
                      <h5 className="font-semibold mb-3">Earnings</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>Hours Worked:</span>
                          <span>{paystub.hours} hours</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Hourly Rate:</span>
                          <span>${paystub.rate}/hour</span>
                        </div>
                        <div className="flex justify-between font-semibold border-t pt-2">
                          <span>Gross Pay:</span>
                          <span>${paystub.grossPay.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Deductions */}
                    <div>
                      <h5 className="font-semibold mb-3">Deductions</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>Federal Tax:</span>
                          <span>${paystub.deductions.federalTax.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>State Tax:</span>
                          <span>${paystub.deductions.stateTax.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Social Security:</span>
                          <span>${paystub.deductions.socialSecurity.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Medicare:</span>
                          <span>${paystub.deductions.medicare.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Health Insurance:</span>
                          <span>${paystub.deductions.healthInsurance.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Dental Insurance:</span>
                          <span>${paystub.deductions.dentalInsurance.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Vision Insurance:</span>
                          <span>${paystub.deductions.visionInsurance.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Simple IRA:</span>
                          <span>${paystub.deductions.simpleIRA.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-semibold border-t pt-2">
                          <span>Total Deductions:</span>
                          <span>${(paystub.grossPay - paystub.netPay).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const renderReviews = () => (
    <div className="card bg-base-100 shadow-lg">
      <div className="card-body">
        <h3 className="card-title flex items-center gap-2 mb-4">
          <FontAwesomeIcon icon={faStar} />
          Performance Reviews
        </h3>
        
        <div className="space-y-6">
          {employeeData.reviews.map((review) => (
            <div key={review.id} className="border border-base-300 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-lg">{review.reviewType}</h4>
                  <p className="text-base-content/60">Review Date: {review.reviewDate}</p>
                  <p className="text-base-content/60">Reviewer: {review.reviewer}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <FontAwesomeIcon 
                        key={i}
                        icon={faStar} 
                        className={`text-sm ${i < Math.floor(review.rating) ? 'text-warning' : 'text-base-content/20'}`}
                      />
                    ))}
                  </div>
                  <div className="text-sm font-semibold">{review.rating}/5.0</div>
                  <span className="badge badge-success badge-sm">{review.status}</span>
                </div>
              </div>

              <div className="mb-4">
                <h5 className="font-semibold mb-2">Comments</h5>
                <p className="text-base-content/80 leading-relaxed">{review.comments}</p>
              </div>

              <div>
                <h5 className="font-semibold mb-2">Goals & Objectives</h5>
                <ul className="list-disc list-inside space-y-1 text-base-content/80">
                  {review.goals.map((goal, index) => (
                    <li key={index}>{goal}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-base-100 flex flex-col">
      <Navbar pageTitle="PROFILE" news={news} recentActivity={recentActivity} />
      <main className="container mx-auto px-4 py-8 flex-1">

        {/* Profile Header */}
        <div className="card bg-base-100 shadow-lg mb-6">
          <div className="card-body">
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-6 min-w-0">
                <div className="w-24 h-24 rounded-full bg-[#0074d9] text-white flex items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faUser} className="text-4xl" style={{marginTop: '8px'}} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold">{profile?.preferredName || profile?.firstName || contact?.preferredName || contact?.name || 'Employee'}{(profile?.lastName || (contact?.name?.split(' ').slice(1).join(' '))) ? ` ${profile?.lastName || contact?.name?.split(' ').slice(1).join(' ')}` : ''}</h2>
                  <p className="text-lg text-base-content/70">{contact?.position || ''}</p>
                  <p className="text-base-content/60">Employee ID: {profile?.employeeId || ''}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {false && (
                  <ProfileQuickAction
                    icon={faCalendarAlt}
                    title="Request PTO"
                    description="Time off and leave"
                    onClick={() => setPtoModalOpen(true)}
                  />
                )}
                <ProfileQuickAction
                  icon={faShieldAlt}
                  title="Request EEAP"
                  description="Tools and equipment"
                  onClick={() => setEeapModalOpen(true)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs tabs-lift mb-6">
          <button 
            className={`tab ${activeTab === 'personal' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('personal')}
          >
            Personal Info
          </button>
          <button 
            className={`tab ${activeTab === 'employment' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('employment')}
          >
            Employment
          </button>
          <button 
            className={`tab ${activeTab === 'certs' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('certs')}
          >
            <FontAwesomeIcon icon={faGraduationCap} className="mr-1" />
            Employee Certs
          </button>
          <button
            className={`tab ${activeTab === 'advances' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('advances')}
          >
            <FontAwesomeIcon icon={faReceipt} className="mr-1" />
            Advances
          </button>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'personal' && renderPersonalInfo()}
          {activeTab === 'employment' && renderEmploymentInfo()}
          {activeTab === 'certs' && renderEmployeeCerts()}
          {activeTab === 'advances' && <ProfileAdvancesTab />}
        </div>
      </main>
      <Footer />
      
      {/* Modals */}
      <PTORequestModal 
        isOpen={ptoModalOpen} 
        onClose={() => setPtoModalOpen(false)} 
      />
      <EEAPModal 
        isOpen={eeapModalOpen} 
        onClose={() => setEeapModalOpen(false)} 
      />
      {confirmPasswordModal}
      {/* Reviews modal removed during HIPAA compliance phase */}
    </div>
  )
}

export default ProfilePage 